import { DateOfSaleMethod, isDateOfSaleMethod } from '@aloft/toolkit-shared';
import { Block, Flex, RadioGroup, SimpleTextArea } from '@aloft/toolkit-sui';

import { Controller, useFormContext } from 'react-hook-form';

import { AdjustmentSettingsHelperText } from '../../components/AdjustmentSettingsHelperText.tsx';
import { DateOfSaleMethodInput } from '../../redux/features/selectors/calculatedMarketAdjustmentSelectors/selectDateOfSaleOrListToSaleAdjustment.ts';
import { selectMarketAdjustmentsCommentaryHelperText } from '../../redux/features/selectors/commentarySelectors/selectMarketAdjustmentsCommentaryHelperText.ts';
import { useAppSelector } from '../../redux/hooks.ts';
import { CustomMonthlyPriceMethodContent } from './CustomMonthlyPriceMethodContent.tsx';
import { CustomMonthlyRateMethodContent } from './CustomMonthlyRateMethodContent.tsx';
import { ZillowMethodContent } from './ZillowMethodContent.tsx';
import { MarketAdjustmentsModalForm } from './types.ts';

const methodRadioItems: { label: string; value: DateOfSaleMethod }[] = [
  { label: 'None', value: 'none' },
  { label: 'Zillow', value: 'zillow' },
  { label: 'Custom Monthly %', value: 'customMonthlyRate' },
  { label: 'Custom Monthly Price', value: 'customMonthlyPrice' },
];

type Props = {
  zillowMethodInput: DateOfSaleMethodInput.Zillow | undefined;
  customMonthlyPriceMethodInput: DateOfSaleMethodInput.CustomMonthlyPrice | undefined;
};

export function DateOfSaleTab({ zillowMethodInput, customMonthlyPriceMethodInput }: Props) {
  const formContext = useFormContext<MarketAdjustmentsModalForm>();
  const selectedMethod = formContext.watch(
    'marketAdjustments.cachedForms.dateOfSale.selectedMethod',
  );
  const helperText = useAppSelector(selectMarketAdjustmentsCommentaryHelperText);

  return (
    <Flex width="100%" paddingX="10px">
      <Flex flexDirection="column" width="200px" flexShrink={0}>
        <Controller
          name="marketAdjustments.cachedForms.dateOfSale.selectedMethod"
          control={formContext.control}
          render={({ field }) => (
            <RadioGroup
              label="Method"
              size="sm"
              gap="8px"
              items={methodRadioItems}
              value={field.value}
              onChange={(value) => {
                if (isDateOfSaleMethod(value)) {
                  field.onChange(value);
                }
              }}
            />
          )}
        />
      </Flex>
      <Flex flexDirection="column" width="100%" gap="16px" overflowX="hidden">
        {selectedMethod === 'zillow' && <ZillowMethodContent input={zillowMethodInput} />}
        {selectedMethod === 'customMonthlyRate' && <CustomMonthlyRateMethodContent />}
        {selectedMethod === 'customMonthlyPrice' && (
          <CustomMonthlyPriceMethodContent input={customMonthlyPriceMethodInput} />
        )}
        {helperText && selectedMethod !== 'none' && (
          <AdjustmentSettingsHelperText helperText={helperText} />
        )}
        <Block maxWidth="600px">
          <SimpleTextArea
            {...formContext.register('commentary')}
            label="Commentary"
            size="lg"
            resize="vertical"
          />
        </Block>
      </Flex>
    </Flex>
  );
}
